import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { User, Mail, Phone, MapPin, Briefcase, Calendar, UserCircle, Save, AlertCircle, Loader2 } from 'lucide-react';
import { userService } from '../services/userService';

const buildFormData = (profileUser: any) => ({
  name: profileUser?.full_name || profileUser?.fullName || profileUser?.name || profileUser?.username || '',
  email: profileUser?.email || '',
  phone: profileUser?.phone || '',
  department: profileUser?.department_name || profileUser?.department || '',
  position: profileUser?.position || '',
  address: profileUser?.address || '',
  emergencyContactName: profileUser?.emergency_contact_name || profileUser?.emergencyContact?.name || '',
  emergencyContactPhone: profileUser?.emergency_contact_phone || profileUser?.emergencyContact?.phone || '',
  emergencyContactRelation: profileUser?.emergency_contact_relation || profileUser?.emergencyContact?.relation || '',
});

const syncStoredUser = (profileUser: any) => {
  const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
  const sessionUser = JSON.parse(sessionStorage.getItem('sessionUser') || '{}');
  const nextUser = {
    ...currentUser,
    ...profileUser,
    full_name: profileUser.full_name,
    role_id: profileUser.role_id,
  };

  localStorage.setItem('currentUser', JSON.stringify(nextUser));
  sessionStorage.setItem('sessionUser', JSON.stringify({ ...sessionUser, ...nextUser }));
};

export function Profile() {
  const { user, updateProfile } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(buildFormData(user));
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [profileUser, setProfileUser] = useState<any>(user);
  const [roleLabel, setRoleLabel] = useState('Kullanici');
  const [roleDescription, setRoleDescription] = useState('');

  useEffect(() => {
    const loadProfile = async () => {
      if (!user?.id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError('');
        const response = await userService.getUser(user.id);
        const loadedUser = response.data || response;
        setProfileUser(loadedUser);
        setFormData(buildFormData(loadedUser));
        setRoleLabel(loadedUser.role_name || loadedUser.role || 'Kullanici');
        setRoleDescription(loadedUser.department_name || '');
      } catch (err) {
        console.error('Failed to load profile:', err);
        setProfileUser(user);
        setFormData(buildFormData(user));
        setRoleLabel(user.role || 'Kullanici');
        setError('Profil bilgileri yuklenirken bir hata olustu');
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.id) return;

    try {
      setSaving(true);
      setError('');

      await userService.updateUser(user.id, {
        full_name: formData.name,
        phone: formData.phone,
        position: formData.position,
        address: formData.address,
        emergency_contact_name: formData.emergencyContactName,
        emergency_contact_phone: formData.emergencyContactPhone,
        emergency_contact_relation: formData.emergencyContactRelation,
      });

      const refreshedResponse = await userService.getUser(user.id);
      const refreshedUser = refreshedResponse.data || refreshedResponse;

      setProfileUser(refreshedUser);
      setFormData(buildFormData(refreshedUser));
      setRoleLabel(refreshedUser.role_name || refreshedUser.role || 'Kullanici');
      setRoleDescription(refreshedUser.department_name || '');
      syncStoredUser(refreshedUser);

      updateProfile({
        name: refreshedUser.full_name || refreshedUser.name || formData.name,
        phone: refreshedUser.phone || '',
        position: refreshedUser.position || '',
        address: refreshedUser.address || '',
        roleId: refreshedUser.role_id?.toString(),
        emergencyContact: {
          name: refreshedUser.emergency_contact_name || '',
          phone: refreshedUser.emergency_contact_phone || '',
          relation: refreshedUser.emergency_contact_relation || '',
        },
      });

      setIsEditing(false);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to update profile:', err);
      setError('Profil kaydedilirken bir hata olustu');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData(buildFormData(profileUser || user));
    setIsEditing(false);
    setError('');
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">Kullanıcı bilgileri yüklenemedi</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] gap-3 text-gray-600">
        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
        <span>Profil bilgileri yukleniyor...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Profilim</h1>
          <p className="text-gray-600 mt-1">Kişisel bilgilerinizi görüntüleyin ve düzenleyin</p>
        </div>
        {!isEditing && (
          <button
            onClick={() => setIsEditing(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Düzenle
          </button>
        )}
      </div>

      {/* Success Message */}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
          <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
            <Save className="w-4 h-4 text-white" />
          </div>
          <p className="text-green-800 font-medium">Profiliniz başarıyla güncellendi!</p>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-600" />
          <p className="text-red-800 font-medium">{error}</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Card */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="text-center">
              <div className="w-24 h-24 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl font-bold text-white">
                  {(formData.name || user.name).split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                </span>
              </div>
              <h2 className="text-xl font-bold text-gray-900">{formData.name || user.name}</h2>
              <p className="text-gray-600 text-sm mt-1">{formData.email || user.email}</p>
              
              {/* Role Badge */}
              <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-full">
                <UserCircle className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-900">
                  {roleLabel}
                </span>
              </div>

              {roleDescription && (
                <p className="text-xs text-gray-500 mt-2">{roleDescription}</p>
              )}
            </div>

            {/* Quick Stats */}
            <div className="mt-6 pt-6 border-t border-gray-200 space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Kullanıcı ID</span>
                <span className="font-medium text-gray-900">{user.id}</span>
              </div>
              {user.startDate && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Başlangıç Tarihi</span>
                  <span className="font-medium text-gray-900">
                    {new Date(user.startDate).toLocaleDateString('tr-TR')}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Profile Information Form */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-6">Kişisel Bilgiler</h3>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <User className="w-4 h-4 inline mr-2" />
                    Ad Soyad
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    disabled={!isEditing}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-600"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Mail className="w-4 h-4 inline mr-2" />
                    E-posta
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    disabled
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
                  />
                  <p className="text-xs text-gray-500 mt-1">E-posta adresi değiştirilemez</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Phone className="w-4 h-4 inline mr-2" />
                    Telefon
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    disabled={!isEditing}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-600"
                    placeholder="+90 5XX XXX XX XX"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Briefcase className="w-4 h-4 inline mr-2" />
                    Departman
                  </label>
                  <input
                    type="text"
                    value={formData.department}
                    onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                    disabled={!isEditing}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-600"
                    placeholder="Lojistik"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Calendar className="w-4 h-4 inline mr-2" />
                    Pozisyon
                  </label>
                  <input
                    type="text"
                    value={formData.position}
                    onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                    disabled={!isEditing}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-600"
                    placeholder="Şoför, Dispozisyon Uzmanı, vb."
                  />
                </div>
              </div>

              {/* Address */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <MapPin className="w-4 h-4 inline mr-2" />
                  Adres
                </label>
                <textarea
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  disabled={!isEditing}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-600"
                  placeholder="Ev adresi"
                />
              </div>

              {/* Emergency Contact */}
              <div className="pt-6 border-t border-gray-200">
                <h4 className="text-sm font-bold text-gray-900 mb-4">Acil Durum İletişim</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Ad Soyad
                    </label>
                    <input
                      type="text"
                      value={formData.emergencyContactName}
                      onChange={(e) => setFormData({ ...formData, emergencyContactName: e.target.value })}
                      disabled={!isEditing}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-600"
                      placeholder="Yakınınızın adı"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Telefon
                    </label>
                    <input
                      type="tel"
                      value={formData.emergencyContactPhone}
                      onChange={(e) => setFormData({ ...formData, emergencyContactPhone: e.target.value })}
                      disabled={!isEditing}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-600"
                      placeholder="+90 5XX XXX XX XX"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Yakınlık
                    </label>
                    <input
                      type="text"
                      value={formData.emergencyContactRelation}
                      onChange={(e) => setFormData({ ...formData, emergencyContactRelation: e.target.value })}
                      disabled={!isEditing}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-600"
                      placeholder="Eş, Anne, Baba, vb."
                    />
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              {isEditing && (
                <div className="flex items-center gap-3 pt-6 border-t border-gray-200">
                    <button
                      type="submit"
                      disabled={saving}
                      className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                    >
                      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      {saving ? 'Kaydediliyor...' : 'Kaydet'}
                    </button>
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    İptal
                  </button>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
